import { FC, useState, useCallback, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
// eslint-disable-next-line import/no-unresolved
import { Area } from 'react-easy-crop/types';
import useBenguide from 'hooks/benguide';
import { useAppSelector } from 'hooks/redux';
import EditableHotspot from 'components/EditableHotspot/EditableHotspot';
import Spinner from 'components/Spinner/Spinner';
import RemoveCustomSection from 'modules/benefitsWellness/components/RemoveCustomComponent/RemoveCustomSection';
import iconEdit from 'images/icon-edit.svg';
import iconDelete from 'images/icons/remove-icon.svg';
import ImageUploader from 'components/ImageUploader/ImageUploader';
import SectionMoveControls from 'modules/customPages/components/SectionMoveControls/SectionMoveControls';
import CustomPageDefaultBannerImage from 'images/samples/default-overview.png';
import OriginalImageCropParams from 'model/OriginalImageCropParams';
import ClickToUploadImage from 'modules/customPages/components/ClickToUploadImage/ClickToUploadImage';

import styles from './imageComponent.module.less';

interface ImageComponentProps {
  id: string;
  imageUrl?: string;
  originalImage?: string;
  imageReference?: string;
  cropArea?: OriginalImageCropParams | null;
  customPageId?: string;
  onSave?: (
    id: string,
    imageUrl: string,
    originalImage: string,
    croppedArea: Area
  ) => void;
  onDelete?: (id: string) => void;
  moveProps?: {
    onMoveUp: () => void;
    onMoveDown: () => void;
    hideUp: boolean;
    hideDown: boolean;
    showControls: boolean;
  };
}

const ImageComponent: FC<ImageComponentProps> = ({
  id,
  imageUrl = '',
  originalImage = '',
  imageReference,
  cropArea,
  customPageId,
  onSave,
  onDelete,
  moveProps,
}) => {
  const { isEditMode, latestRevision, revision } = useBenguide();
  const customPagesState = useAppSelector((state) => state.customPages);
  const { imageContentUrls, imageUploadInProgress } = customPagesState;

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [loadingImage, setLoadingImage] = useState(false);
  const imageUploaderRef = useRef<any>(null);

  const displayImageUrl = useMemo(() => {
    // Always use the most current revision to prevent showing stale images
    const currentRevision = latestRevision || revision;

    // Try to get the cached URL from Redux state first
    if (customPageId && currentRevision) {
      const key = `${customPageId}-${id}-cropped-${currentRevision}`;
      const cachedUrl = imageContentUrls[key];
      if (cachedUrl) {
        return cachedUrl;
      }
    }

    // Fallback to prop value
    const reduxImageUrl = imageUrl || '';

    return reduxImageUrl;
  }, [imageUrl, id, imageContentUrls, latestRevision, revision, customPageId]);

  const displayOriginalImage = useMemo(() => {
    // Always use the most current revision to prevent showing stale images
    const currentRevision = latestRevision || revision;

    // Try to get the cached original URL from Redux state first
    if (customPageId && currentRevision) {
      const key = `${customPageId}-${id}-original-${currentRevision}`;
      const cachedUrl = imageContentUrls[key];
      if (cachedUrl) {
        return cachedUrl;
      }
    }

    // Fallback to prop value
    return originalImage || '';
  }, [
    originalImage,
    id,
    imageContentUrls,
    latestRevision,
    revision,
    customPageId,
  ]);

  const showAddImageBtn = isEmpty(imageReference) && isEditMode;

  // Close ImageUploader modal when upload completes (using Redux imageUploadInProgress)
  useEffect(() => {
    if (imageReference && !imageUploadInProgress && loadingImage) {
      // Upload completed successfully (imageReference updated + upload finished), close the modal
      if (imageUploaderRef?.current?.close) {
        imageUploaderRef.current.close();
      }
      setLoadingImage(false);
    }
  }, [imageReference, imageUploadInProgress, loadingImage]);

  // Convert OriginalImageCropParams to Area type for ImageUploader
  const defaultCropArea = useMemo((): Area | undefined => {
    if (!cropArea) return undefined;
    return {
      x: cropArea.x,
      y: cropArea.y,
      width: cropArea.width,
      height: cropArea.height,
    };
  }, [cropArea]);

  const handleSave = async (
    newImageUrl: string,
    newOriginalImage: string,
    croppedArea: Area
  ) => {
    // Prevent double API calls (following NewHireChecklist pattern)
    if (loadingImage) {
      return;
    }

    try {
      setLoadingImage(true);
      // Use the onSave prop (following NewHireChecklist pattern)
      if (onSave) {
        await onSave(id, newImageUrl, newOriginalImage, croppedArea);
        // Note: Modal closing is handled by useEffect when upload completes
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setLoadingImage(false); // Reset on error
    }
  };

  const handleDelete = useCallback(() => {
    setShowDeleteConfirmation(true);
  }, []);

  const confirmDelete = useCallback(() => {
    if (onDelete) {
      onDelete(id);
    }
    setShowDeleteConfirmation(false);
  }, [id, onDelete]);

  return (
    <div
      className={`${styles.imageComponentWrapper} ${
        !isEditMode || !isEmpty(displayImageUrl) ? styles.previewMode : ''
      }`}
    >
      {moveProps && (
        <SectionMoveControls
          onMoveUp={moveProps.onMoveUp}
          onMoveDown={moveProps.onMoveDown}
          hideUp={moveProps.hideUp}
          hideDown={moveProps.hideDown}
          showControls={moveProps.showControls}
        />
      )}
      <div className={styles.imageComponentContainer}>
        <div
          className={`${styles.imageContentArea} ${
            displayImageUrl ? styles.hasImage : ''
          }`}
        >
          {loadingImage ? (
            <Spinner />
          ) : (
            <>
              {showAddImageBtn ? (
                <EditableHotspot
                  editText="Edit"
                  alwaysVisible={false}
                  editIcon={<img src={iconEdit} alt="edit" />}
                  deleteText="Remove"
                  deleteIcon={<img src={iconDelete} alt="delete" />}
                  onDelete={handleDelete}
                  showDeleteButton={isEditMode}
                  className="fullWidthHotspot"
                  disabled={imageUploadInProgress}
                  customModal={(visible: boolean, onClose: () => void) => (
                    <ImageUploader
                      title="Image Block"
                      cropShape="rect"
                      uploadedImage={displayImageUrl}
                      unCroppedImage={displayOriginalImage}
                      aspect={1}
                      loading={false}
                      onConfirm={(
                        image: string,
                        originalImage: string,
                        croppedArea: Area
                      ) => {
                        handleSave(image, originalImage, croppedArea);
                      }}
                      showOperations
                      uploadNewImgText="+ Upload New Photo"
                      isOpenImageTray={visible}
                      onCloseImgTray={onClose}
                      uploadImgAfterResetText="Upload Image"
                      hideUploader
                      hideImagePreview={true}
                      customCropper={{ width: 780, height: 248 }}
                      defaultLogo={CustomPageDefaultBannerImage}
                      imagePreviewStyle={styles.customPageMainImage}
                      uploadWrapperClass={styles.customPageUploadWrapper}
                      maxSizeMB={4}
                      defaultCropArea={defaultCropArea}
                      preventCloseOnConfirm={true}
                      saving={imageUploadInProgress}
                      customPageMode={true}
                      ref={imageUploaderRef}
                    />
                  )}
                >
                  <div className={styles.addImageButton}>
                    <ClickToUploadImage />
                  </div>
                </EditableHotspot>
              ) : displayImageUrl ? (
                <EditableHotspot
                  alwaysVisible={false}
                  editText="Edit"
                  editIcon={<img src={iconEdit} alt="edit" />}
                  deleteText="Remove"
                  deleteIcon={<img src={iconDelete} alt="delete" />}
                  onDelete={handleDelete}
                  showDeleteButton={isEditMode}
                  className="fullWidthHotspot"
                  disabled={imageUploadInProgress}
                  customModal={(visible: boolean, onClose: () => void) => (
                    <ImageUploader
                      title="Image Block"
                      cropShape="rect"
                      uploadedImage={displayImageUrl}
                      unCroppedImage={displayOriginalImage}
                      aspect={1}
                      loading={false}
                      onConfirm={(
                        image: string,
                        originalImage: string,
                        croppedArea: Area
                      ) => {
                        handleSave(image, originalImage, croppedArea);
                      }}
                      showOperations
                      uploadNewImgText="+ Upload New Photo"
                      isOpenImageTray={visible}
                      onCloseImgTray={onClose}
                      uploadImgAfterResetText="Upload Image"
                      hideUploader
                      hideImagePreview={true}
                      customCropper={{ width: 780, height: 248 }}
                      defaultLogo={CustomPageDefaultBannerImage}
                      imagePreviewStyle={styles.customPageMainImage}
                      uploadWrapperClass={styles.customPageUploadWrapper}
                      maxSizeMB={4}
                      defaultCropArea={defaultCropArea}
                      preventCloseOnConfirm={true}
                      saving={imageUploadInProgress}
                      customPageMode={true}
                      ref={imageUploaderRef}
                    />
                  )}
                >
                  <div className={styles.imagePreview}>
                    <img
                      src={displayImageUrl}
                      alt="Custom page image"
                      className={styles.uploadedImage}
                    />
                  </div>
                </EditableHotspot>
              ) : imageReference ? (
                // Show loading spinner when image exists but URL is being fetched
                <div className={styles.imageLoadingContainer}>
                  <Spinner />
                  <p>Loading image...</p>
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <RemoveCustomSection
        visible={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onRemove={confirmDelete}
      />
    </div>
  );
};

ImageComponent.propTypes = {
  id: PropTypes.string.isRequired,
  imageUrl: PropTypes.string,
  originalImage: PropTypes.string,
  imageReference: PropTypes.string,
  customPageId: PropTypes.string,
  cropArea: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
  }),
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
  moveProps: PropTypes.shape({
    onMoveUp: PropTypes.func.isRequired,
    onMoveDown: PropTypes.func.isRequired,
    hideUp: PropTypes.bool.isRequired,
    hideDown: PropTypes.bool.isRequired,
    showControls: PropTypes.bool.isRequired,
  }),
};

ImageComponent.defaultProps = {
  imageUrl: '',
  originalImage: '',
  imageReference: '',
};

export default ImageComponent;
